import React, { useState, useEffect } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";

// Valid DL tenures — kept in sync with backend/utils/dlCalculations.js DL_TENURES
const DL_TENURES = [100, 115, 120, 125];

/**
 * Step 1 of the Daily Loan Branch Manager disbursement flow.
 *
 * Collects:
 *   collectionTime       – "HH:MM" daily collection time
 *   installmentStartDate – YYYY-MM-DD first collection day
 *   tenureInMonths       – instalment count (one of DL_TENURES)
 *
 * Pre-filled with initial values when editing existing terms.
 */
const SaveDlTermsModal = ({
  show,
  handleClose,
  memberId,
  initialCollectionTime,
  initialStartDate,
  initialTenure,
  onSuccess,
}) => {
  const [collectionTime, setCollectionTime] = useState("");
  const [installmentStartDate, setInstallmentStartDate] = useState("");
  const [tenureInMonths, setTenureInMonths] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setCollectionTime(
        initialCollectionTime ? initialCollectionTime.slice(0, 5) : ""
      );
      setInstallmentStartDate(initialStartDate || "");
      setTenureInMonths(initialTenure ? String(initialTenure) : "");
      setError("");
    }
  }, [show, initialCollectionTime, initialStartDate, initialTenure]);

  const handleConfirm = async () => {
    setError("");

    if (!collectionTime) {
      setError("Please enter the daily collection time.");
      return;
    }
    if (!installmentStartDate) {
      setError("Please enter the first collection date.");
      return;
    }
    if (!tenureInMonths) {
      setError("Please select the loan tenure.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/api/saveDlDisbursementTerms`, {
        memberId,
        collectionTime,
        installmentStartDate,
        tenureInMonths: Number(tenureInMonths),
      });
      // Reset fields so the modal is clean if re-opened
      setCollectionTime("");
      setInstallmentStartDate("");
      setTenureInMonths("");
      handleClose();
      onSuccess();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to save loan terms. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Daily Loan — Save Loan Terms</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <p className="text-muted mb-3">
          Enter the collection schedule before taking the customer's signature.
          The loan ID will be generated once you save.
        </p>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Daily Collection Time <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="time"
              value={collectionTime}
              onChange={(e) => setCollectionTime(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              First Collection Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={installmentStartDate}
              onChange={(e) => setInstallmentStartDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Loan Tenure (days) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={tenureInMonths}
              onChange={(e) => setTenureInMonths(e.target.value)}
            >
              <option value="">-- Select Tenure --</option>
              {DL_TENURES.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={loading}>
          {loading ? "Saving…" : "Save Terms"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveDlTermsModal;
